import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ExternalLink,
  Linkedin,
  Mail,
  MessageSquare,
  ThumbsUp,
  Repeat2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicLanding — Tarang Gopani's authoritative personal brand page.
 *
 * Design north star: Financial Times / Bain partner profile. Ivory background,
 * deep-navy typography, hairline gold accents. Editorial feel — every element
 * carries CFO-grade authority without being flashy. NO dark mode, NO gradients
 * on the hero, NO stock-photo aesthetic.
 */
export default function PublicLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company: "", message: "" });
  const [captured, setCaptured] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/site/public/${slug}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else console.error(err);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const submitCapture = async (e, source = "footer") => {
    e?.preventDefault();
    if (!form.email) return toast.error("Email required");
    setCaptureBusy(true);
    try {
      await axios.post(`${API}/api/site/capture`, {
        slug,
        email: form.email,
        name: form.name,
        company: form.company,
        message: form.message,
        source,
      });
      setCaptured(true);
      toast.success("Thanks — I'll be in touch shortly.");
      setForm({ email: "", name: "", company: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setCaptureBusy(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#fbf9f5] text-[#0b1f3a] flex items-center justify-center">
        <div className="text-sm text-[#0b1f3a]/50">Loading…</div>
      </div>
    );
  if (notFound)
    return (
      <div className="min-h-screen bg-[#fbf9f5] text-[#0b1f3a] flex items-center justify-center">
        <div className="text-center">
          <div className="text-3xl font-serif mb-2" style={{ fontFamily: "'Fraunces', serif" }}>
            Not found
          </div>
          <div className="text-sm text-[#0b1f3a]/60">This page does not exist.</div>
        </div>
      </div>
    );

  const c = data.config;
  const posts = data.posts || [];
  const highlights = data.highlights || [];
  const newsletters = data.newsletters || [];

  const SERIF = { fontFamily: "'Fraunces', 'Times New Roman', serif" };
  const SANS = { fontFamily: "'Inter', system-ui, sans-serif" };

  const LABEL =
    "text-[11px] uppercase tracking-[0.22em] text-[#a67c00] font-semibold";
  const DIVIDER = "border-t border-[#e5dfd0]";

  return (
    <div className="min-h-screen bg-[#fbf9f5] text-[#0b1f3a] antialiased" style={SANS}>
      {/* ─────────── TOP RULE ─────────── */}
      <div className="border-t-2 border-[#0b1f3a]" />

      {/* ─────────── HEADER STRIP ─────────── */}
      <header className="max-w-6xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between text-xs" data-testid="landing-header">
        <div className="tracking-[0.24em] uppercase text-[#0b1f3a]/70 font-semibold">
          {c.display_name}
        </div>
        <nav className="hidden sm:flex items-center gap-8 text-[#0b1f3a]/60">
          <a href="#work" className="hover:text-[#a67c00] transition-colors">Work</a>
          <a href="#writing" className="hover:text-[#a67c00] transition-colors">Writing</a>
          <a href="#contact" className="hover:text-[#a67c00] transition-colors">Contact</a>
        </nav>
      </header>
      <div className={DIVIDER} />

      {/* ─────────── HERO ─────────── */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="landing-hero">
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-14 items-start">
          <div>
            <div className={LABEL}>Cost transformation · Board advisory · CFO peer</div>
            <h1
              className="text-5xl sm:text-6xl lg:text-[88px] leading-[1.02] tracking-[-0.02em] mt-6 mb-8 text-[#0b1f3a]"
              style={{ ...SERIF, fontWeight: 500 }}
            >
              {c.display_name}
            </h1>
            {c.headline && (
              <p
                className="text-xl lg:text-2xl text-[#334e6c] leading-[1.45] max-w-2xl mb-10"
                style={SERIF}
              >
                {c.headline}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-4">
              {c.calendar_url && (
                <a
                  href={c.calendar_url}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-2 bg-[#0b1f3a] text-[#fbf9f5] px-7 py-3.5 text-sm font-medium hover:bg-[#a67c00] transition-colors tracking-wide"
                  data-testid="landing-cta-calendar"
                >
                  {c.cta_label || "Request a consultation"}
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2} />
                </a>
              )}
              {c.era_group_url && (
                <a
                  href={c.era_group_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 border-b border-[#0b1f3a]/40 hover:border-[#a67c00] hover:text-[#a67c00] pb-1 text-sm text-[#0b1f3a] transition-colors"
                  data-testid="landing-link-era"
                >
                  ERA Group profile
                  <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                </a>
              )}
              {c.linkedin_url && (
                <a
                  href={c.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[#0b1f3a]/70 hover:text-[#a67c00] text-sm transition-colors"
                  data-testid="landing-link-linkedin"
                >
                  <Linkedin className="h-4 w-4" strokeWidth={1.75} />
                  LinkedIn
                </a>
              )}
            </div>

            {/* Credentials strip — establishes authority under the CTA */}
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl">
              <div>
                <div className={LABEL}>Saved</div>
                <div className="text-2xl mt-1" style={{ ...SERIF, fontWeight: 500 }}>$180M</div>
              </div>
              <div>
                <div className={LABEL}>Years</div>
                <div className="text-2xl mt-1" style={{ ...SERIF, fontWeight: 500 }}>15+</div>
              </div>
              <div>
                <div className={LABEL}>Clients</div>
                <div className="text-2xl mt-1" style={{ ...SERIF, fontWeight: 500 }}>47+</div>
              </div>
              <div>
                <div className={LABEL}>Coverage</div>
                <div className="text-2xl mt-1" style={{ ...SERIF, fontWeight: 500 }}>AU · NZ</div>
              </div>
            </div>
          </div>

          {c.photo_url && (
            <div className="relative order-first lg:order-none">
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute -top-3 -left-3 w-full h-full border border-[#a67c00]/60"
                />
                <img
                  src={c.photo_url}
                  alt={c.display_name}
                  className="relative w-full aspect-[4/5] object-cover"
                  data-testid="landing-photo"
                />
              </div>
              <div className="mt-5 text-xs text-[#334e6c] leading-relaxed max-w-xs">
                Based in Sydney. Working with mid-market and ASX-listed CFOs across Australia and New Zealand.
              </div>
            </div>
          )}
        </div>
      </section>
      <div className={DIVIDER} />

      {/* ─────────── BIO ─────────── */}
      {c.bio && (
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="landing-bio">
          <div className="grid lg:grid-cols-[220px_1fr] gap-12">
            <div className={LABEL}>About</div>
            <div>
              <div
                className="text-2xl lg:text-3xl leading-[1.5] text-[#0b1f3a] whitespace-pre-line max-w-3xl"
                style={{ ...SERIF, fontWeight: 400 }}
              >
                {c.bio}
              </div>
            </div>
          </div>
        </section>
      )}
      <div className={DIVIDER} />

      {/* ─────────── CASE STUDIES ─────────── */}
      {c.case_studies && c.case_studies.length > 0 && (
        <section
          id="work"
          className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
          data-testid="landing-cases"
        >
          <div className="grid lg:grid-cols-[220px_1fr] gap-12 mb-14">
            <div className={LABEL}>Selected engagements</div>
            <h2 className="text-4xl lg:text-5xl tracking-tight" style={{ ...SERIF, fontWeight: 500 }}>
              Where CFOs recovered millions
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-[#e5dfd0]">
            {c.case_studies.map((cs, i) => (
              <div
                key={i}
                className={`py-12 lg:px-6 ${i < c.case_studies.length - 1 ? "lg:border-r border-[#e5dfd0]" : ""} ${i > 0 ? "border-t lg:border-t-0 border-[#e5dfd0]" : ""} ${i % 2 === 1 ? "md:border-t-0 md:border-l" : ""}`}
                data-testid={`landing-case-${i}`}
              >
                <div className="text-5xl lg:text-6xl text-[#0b1f3a] mb-4" style={{ ...SERIF, fontWeight: 500, letterSpacing: "-0.02em" }}>
                  {cs.result || "—"}
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#a67c00] mb-4 font-semibold">
                  {cs.sector || cs.industry}
                </div>
                <div className="text-[#334e6c] leading-[1.6] text-[14px]">{cs.summary}</div>
                {cs.sector && cs.industry && (
                  <div className="text-xs text-[#0b1f3a]/40 mt-4 font-mono">
                    — {cs.industry}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      <div className={DIVIDER} />

      {/* ─────────── HIGHLIGHTS (top-3, weekly auto-pinned) ─────────── */}
      {highlights.length > 0 && (
        <section
          className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
          data-testid="landing-highlights"
        >
          <div className="grid lg:grid-cols-[220px_1fr] gap-12 mb-12">
            <div className={LABEL}>Highlights · this week</div>
            <div>
              <h2 className="text-4xl lg:text-5xl tracking-tight" style={{ ...SERIF, fontWeight: 500 }}>
                What CFOs are engaging with
              </h2>
              <p className="text-[#334e6c] mt-4 max-w-2xl leading-relaxed">
                The three posts driving the most reactions, comments and reshares in my LinkedIn feed right now.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-0 border-t border-[#e5dfd0]">
            {highlights.slice(0, 3).map((p, idx) => (
              <a
                key={p.post_id}
                href={p.share_url || "#"}
                target="_blank"
                rel="noreferrer"
                className={`group relative py-12 lg:px-8 hover:bg-[#f4efe2] transition-colors ${idx < 2 ? "md:border-r border-[#e5dfd0]" : ""} ${idx > 0 ? "border-t md:border-t-0 border-[#e5dfd0]" : ""}`}
                data-testid={`landing-highlight-${p.post_id}`}
              >
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#a67c00] mb-6 font-semibold">
                  № {String(idx + 1).padStart(2, "0")}
                </div>
                <div
                  className="text-lg text-[#0b1f3a] leading-[1.55] mb-6 line-clamp-6 whitespace-pre-line"
                  style={{ ...SERIF, fontWeight: 400 }}
                >
                  {p.text}
                </div>
                <div className="flex items-center gap-5 text-xs text-[#334e6c] mb-4 border-t border-[#e5dfd0] pt-4">
                  <span className="flex items-center gap-1.5">
                    <ThumbsUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {p.reactions || 0}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {p.comments || 0}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Repeat2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {p.reposts || 0}
                  </span>
                  {p.posted_at && (
                    <span className="ml-auto font-mono text-[#0b1f3a]/40">
                      {new Date(p.posted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
                <div className="inline-flex items-center gap-1 text-xs text-[#a67c00] font-semibold group-hover:gap-2 transition-all">
                  Read on LinkedIn <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
      <div className={DIVIDER} />

      {/* ─────────── NEWSLETTERS ─────────── */}
      {newsletters.length > 0 && (
        <section
          id="writing"
          className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
          data-testid="landing-newsletters"
        >
          <div className="grid lg:grid-cols-[220px_1fr] gap-12 mb-12">
            <div className={LABEL}>The Newsletter</div>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <h2 className="text-4xl lg:text-5xl tracking-tight" style={{ ...SERIF, fontWeight: 500 }}>
                Essays for finance leaders
              </h2>
              {c.linkedin_url && (
                <a
                  href={c.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-[#0b1f3a] hover:text-[#a67c00] flex items-center gap-1.5 border-b border-[#0b1f3a]/40 hover:border-[#a67c00] pb-1"
                >
                  Subscribe on LinkedIn <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </a>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-[#e5dfd0]">
            {newsletters.slice(0, 6).map((n, i) => (
              <a
                key={n.id}
                href={n.url || "#"}
                target="_blank"
                rel="noreferrer"
                className={`group py-8 lg:px-8 hover:bg-[#f4efe2] transition-colors border-b border-[#e5dfd0] lg:border-b-0 ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""} lg:${i < 3 ? "border-b" : ""}`}
                data-testid={`landing-newsletter-${n.id}`}
              >
                <div className="text-xs text-[#0b1f3a]/40 mb-3 font-mono uppercase tracking-wider">
                  {n.published_at ? new Date(n.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : ""}
                </div>
                <div
                  className="text-2xl leading-[1.25] mb-3 group-hover:text-[#a67c00] transition-colors"
                  style={{ ...SERIF, fontWeight: 500 }}
                >
                  {n.title}
                </div>
                {n.summary && (
                  <div className="text-sm text-[#334e6c] leading-relaxed line-clamp-3">
                    {n.summary}
                  </div>
                )}
                <div className="inline-flex items-center gap-1 text-xs text-[#a67c00] font-semibold mt-4">
                  Read <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
      <div className={DIVIDER} />

      {/* ─────────── LINKEDIN POSTS ─────────── */}
      {posts.length > 0 && (
        <section
          className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
          data-testid="landing-posts"
        >
          <div className="grid lg:grid-cols-[220px_1fr] gap-12 mb-12">
            <div className={LABEL}>Recent thinking</div>
            <h2 className="text-4xl lg:text-5xl tracking-tight" style={{ ...SERIF, fontWeight: 500 }}>
              From the feed
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-0 border-t border-[#e5dfd0]">
            {posts.slice(0, 6).map((p, i) => (
              <a
                key={p.post_id}
                href={p.share_url || "#"}
                target="_blank"
                rel="noreferrer"
                className={`group py-8 lg:px-8 hover:bg-[#f4efe2] transition-colors border-b border-[#e5dfd0] ${i % 2 === 0 ? "md:border-r" : ""}`}
                data-testid={`landing-post-${p.post_id}`}
              >
                <div className="text-xs text-[#0b1f3a]/50 mb-3 flex items-center gap-4">
                  <span className="font-mono">
                    {p.posted_at && new Date(p.posted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" strokeWidth={1.5} />{p.reactions || 0}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" strokeWidth={1.5} />{p.comments || 0}</span>
                  </span>
                </div>
                <div className="text-[#0b1f3a]/90 leading-relaxed whitespace-pre-line line-clamp-5">
                  {p.text}
                </div>
                <div className="inline-flex items-center gap-1 text-xs text-[#a67c00] font-semibold mt-4">
                  Read on LinkedIn <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
      <div className={DIVIDER} />

      {/* ─────────── TESTIMONIALS ─────────── */}
      {c.testimonials && c.testimonials.length > 0 && (
        <section
          className="max-w-4xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
          data-testid="landing-testimonials"
        >
          <div className={`${LABEL} mb-12`}>What clients say</div>
          <div className="space-y-16">
            {c.testimonials.map((t, i) => (
              <blockquote key={i} className="relative pl-8 border-l-2 border-[#a67c00]">
                <div
                  className="text-2xl lg:text-3xl leading-[1.45] text-[#0b1f3a] mb-4"
                  style={{ ...SERIF, fontWeight: 400 }}
                >
                  {t.quote}
                </div>
                <div className="text-sm text-[#334e6c]">
                  <span className="font-semibold text-[#0b1f3a]">{t.name}</span>
                  {t.role ? ` · ${t.role}` : ""}
                  {t.company ? ` · ${t.company}` : ""}
                </div>
              </blockquote>
            ))}
          </div>
        </section>
      )}
      <div className={DIVIDER} />

      {/* ─────────── EMAIL CAPTURE ─────────── */}
      <section
        id="contact"
        className="bg-[#0b1f3a] text-[#fbf9f5]"
        data-testid="landing-capture"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#e4b846] font-semibold mb-6">
            Direct line
          </div>
          <h2
            className="text-4xl lg:text-5xl mb-6 tracking-tight"
            style={{ ...SERIF, fontWeight: 500 }}
          >
            {c.email_capture_headline || "Have a cost problem worth solving?"}
          </h2>
          <p className="text-[#fbf9f5]/70 text-lg mb-12 max-w-xl leading-relaxed">
            {c.email_capture_subhead ||
              "One direct line. I read every note and respond within 24 hours if there's a fit."}
          </p>
          {captured ? (
            <div className="text-[#e4b846] text-lg" data-testid="landing-capture-thanks">
              Thanks — you&apos;re in. I&apos;ll be in touch shortly.
            </div>
          ) : (
            <form
              onSubmit={(e) => submitCapture(e, "hero")}
              className="max-w-xl space-y-3"
            >
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-transparent border-[#fbf9f5]/25 text-[#fbf9f5] placeholder:text-[#fbf9f5]/40 h-12 rounded-none focus-visible:ring-[#e4b846]"
                  data-testid="landing-capture-name"
                />
                <Input
                  type="text"
                  placeholder="Company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-transparent border-[#fbf9f5]/25 text-[#fbf9f5] placeholder:text-[#fbf9f5]/40 h-12 rounded-none focus-visible:ring-[#e4b846]"
                  data-testid="landing-capture-company"
                />
              </div>
              <Input
                type="email"
                placeholder="Email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-transparent border-[#fbf9f5]/25 text-[#fbf9f5] placeholder:text-[#fbf9f5]/40 h-12 rounded-none focus-visible:ring-[#e4b846]"
                data-testid="landing-capture-email"
              />
              <Textarea
                rows={3}
                placeholder="What are you trying to solve? (optional)"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-transparent border-[#fbf9f5]/25 text-[#fbf9f5] placeholder:text-[#fbf9f5]/40 rounded-none focus-visible:ring-[#e4b846]"
                data-testid="landing-capture-message"
              />
              <Button
                type="submit"
                disabled={captureBusy}
                className="w-full h-12 bg-[#e4b846] text-[#0b1f3a] hover:bg-[#f2c85c] font-semibold text-base rounded-none"
                data-testid="landing-capture-submit"
              >
                <Mail className="h-4 w-4 mr-2" strokeWidth={2} />
                {captureBusy ? "Sending…" : "Send"}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ─────────── FOOTER ─────────── */}
      <footer className="max-w-6xl mx-auto px-6 lg:px-12 py-10 text-xs text-[#0b1f3a]/50 flex flex-wrap items-center justify-between gap-4">
        <div>© {new Date().getFullYear()} {c.display_name}. All rights reserved.</div>
        <div className="flex items-center gap-6">
          {c.linkedin_url && (
            <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="hover:text-[#a67c00]">LinkedIn</a>
          )}
          {c.era_group_url && (
            <a href={c.era_group_url} target="_blank" rel="noreferrer" className="hover:text-[#a67c00]">ERA Group</a>
          )}
        </div>
      </footer>

      {/* Editorial typography */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&display=swap"
      />
    </div>
  );
}
